import "@/App.css";
import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { api } from "./lib/api";
import Layout from "./components/Layout";
import { Login, Register } from "./pages/Auth";
import { ForgotPassword, ResetPassword, ForcePasswordChange } from "./pages/ForgotReset";
import { Dashboard } from "./pages/Dashboard";
import { Invoices } from "./pages/Invoices";
import { Transactions, Crops, Inventory, Customers } from "./pages/Modules";
import { Payroll } from "./pages/Payroll";
import { Employees } from "./pages/Employees";
import { Admin } from "./pages/Admin";
import { Sales } from "./pages/Sales";
import { Products } from "./pages/Products";
import { Documents } from "./pages/Documents";
import { Landing } from "./pages/Landing";
import { Shop } from "./pages/Shop";
import { Orders } from "./pages/Orders";
import { Reports } from "./pages/Reports";
import { Vendors, Bills } from "./pages/VendorsBills";
import { LivestockHub } from "./pages/LivestockHub";
import { WebsiteContent } from "./pages/WebsiteContent";
import { canAccess } from "./lib/permissions";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { Leaf, Lock } from "lucide-react";

function Loader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="flex h-12 w-12 animate-pulse items-center justify-center rounded-xl bg-primary">
          <Leaf className="h-6 w-6 text-white" />
        </div>
        <p className="text-sm text-muted-foreground">Loading Modav Farms…</p>
      </div>
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sessionId } });
        setUser(data);
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 403) {
          toast.info(typeof detail === "string" ? detail : "Your account is pending admin approval.");
        }
        window.history.replaceState(null, "", "/login");
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return <Loader />;
}

function Protected({ children, module }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (module && !canAccess(user.role, module)) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] flex-col items-center justify-center text-center" data-testid="no-access">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
            <Lock className="h-6 w-6 text-muted-foreground" />
          </div>
          <h2 className="font-display text-xl font-bold">Access restricted</h2>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            Your role doesn't have permission to view this module. Contact your administrator if you need access.
          </p>
        </div>
      </Layout>
    );
  }
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  if (window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/shop" element={<Shop />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<Protected module="dashboard"><Dashboard /></Protected>} />
      <Route path="/sales" element={<Protected module="sales"><Sales /></Protected>} />
      <Route path="/products" element={<Protected module="products"><Products /></Protected>} />
      <Route path="/orders" element={<Protected module="orders"><Orders /></Protected>} />
      <Route path="/invoices" element={<Protected module="invoices"><Invoices /></Protected>} />
      <Route path="/transactions" element={<Protected module="transactions"><Transactions /></Protected>} />
      <Route path="/bills" element={<Protected module="bills"><Bills /></Protected>} />
      <Route path="/vendors" element={<Protected module="vendors"><Vendors /></Protected>} />
      <Route path="/customers" element={<Protected module="customers"><Customers /></Protected>} />
      <Route path="/reports" element={<Protected module="reports"><Reports /></Protected>} />
      <Route path="/documents" element={<Protected module="documents"><Documents /></Protected>} />
      <Route path="/livestock" element={<Protected module="livestock"><LivestockHub /></Protected>} />
      <Route path="/crops" element={<Protected module="crops"><Crops /></Protected>} />
      <Route path="/inventory" element={<Protected module="inventory"><Inventory /></Protected>} />
      <Route path="/employees" element={<Protected module="employees"><Employees /></Protected>} />
      <Route path="/website" element={<Protected module="content"><WebsiteContent /></Protected>} />
      <Route path="/payroll" element={<Protected module="payroll"><Payroll /></Protected>} />
      <Route path="/admin" element={<Protected module="admin"><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
